import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import clsx from 'clsx'
import RcTable, { type TableProps as RCTableProps } from 'rc-table'
import type { ColumnType } from 'rc-table/lib/interface'
import React, { useRef, type HTMLAttributes } from 'react'
import { useTranslation } from 'react-i18next'

import ChevronSmallDown from '@/assets/icons/chevron-small-down.svg?react'
import ChevronSmallUp from '@/assets/icons/chevron-small-up.svg?react'
import MoreOutlined from '@/assets/icons/menu-vertical.svg?react'
import TriangleDown from '@/assets/icons/triangle-down.svg?react'
import TriangleUp from '@/assets/icons/triangle-up.svg?react'
import useStickyElement, { PRIORITY } from '@/hooks/useStickyElement'
import type { Order, Sorting } from '@/types/sorting'
import type { StringKeys } from '@/types/string-keys'

import styles from './Table.module.scss'
import { TABLE_CELL_WIDTH } from './table-cell-width'
import ButtonIcon from '../ButtonIcon/ButtonIcon'
import Checkbox from '../Checkbox/Checkbox'
import Divider from '../Divider/Divider'
import Dropdown, { DropdownMenuItem } from '../Dropdown/Dropdown'
import Label from '../Label'
import Pagination from '../Pagination/Pagination'
import Select from '../Select/Select'
import Spinner from '../Spinner'
import TableTopBarCount from '../TableTopBar/TableTopBarCount'

const selectOptions = [
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '30', value: 30 },
  { label: '40', value: 40 }
]

export type Column<TData> = Omit<ColumnType<TData>, 'key'> & {
  sorterKey?: string
  key: string
  isHighlighted?: boolean
  filled?: boolean
  cellDataTestId?: string
  label?: string
  alwaysVisible?: boolean
}

export type TableAction<TData> = {
  text: React.ReactNode
  icon?: React.ReactNode
  variantAction?: 'normal' | 'danger'
  onClick: (item: TData) => void
  isHidden?: boolean | ((item: TData) => boolean)
}

export type RowSelection<TData extends Record<string, unknown>> = {
  disabled?: boolean
  count: number
  selectedItems: string[]
  allCheckboxValue: boolean | 'mixed'
  labelHeader: string | ((selected: boolean) => string)
  labelItem: (row: TData & { selected: boolean }) => string
  onSelect: (value: boolean, rowId: string) => void
  onSelectAll: (value: boolean) => void
  checkboxVariant?: 'dark' | 'default'
}

export type TableProps<TData extends Record<string, unknown>> = {
  data: TData[]
  id: string
  rowKey?: StringKeys<TData>
  pagination?: {
    page: number
    pageSize: number
  }
  count?: number
  totalCount?: number
  tableClassName?: string
  sorting?: Sorting
  actions?: TableAction<TData>[] | ((row: TData) => TableAction<TData>[])
  onRowClick?: (row: TData) => void
  onChangePage?: (page: number) => void
  onChangeSorting?: (sorting: Sorting) => void
  onChangePageSize?: (pageSize: number) => void
  columns: Column<TData>[]
  tableHeight?: string | number
  tableWidth?: string | number
  showTopBar?: boolean
  leftTopBar?: React.ReactNode
  rightTopBar?: React.ReactNode
  isLoading?: boolean
  expandedRowRender?: (record: TData) => React.ReactNode
  rowSelection?: RowSelection<TData>
  withBorders?: boolean
  noRowHoverEffect?: boolean
  components?: RCTableProps<TData>['components']
  showHeader?: boolean
  rowClassName?: string
  headerTop?: number
  topBarTop?: number
  caption: string
}

const Table = <TData extends Record<string, unknown>>(
  props: TableProps<TData>
) => {
  const refTable = useRef<HTMLDivElement>(null)
  const { showTopBar = true } = props

  const headerTop = useStickyElement({
    elementRef: refTable,
    priority: PRIORITY.tableHeader
  })

  const header = refTable.current?.querySelector(
    '.rc-table-header'
  ) as HTMLTableElement
  const body = refTable.current?.querySelector(
    '.rc-table-body'
  ) as HTMLTableElement

  const { t } = useTranslation(['common'])

  const [expandedRowKeys, setExpandedRowKeys] = React.useState<React.Key[]>([])

  const changeSorting = (column: Column<TData>) => {
    const isCurrentColumnSorted = props.sorting?.key === column.sorterKey

    const order = isCurrentColumnSorted
      ? currentOrder === 'asc'
        ? 'desc'
        : undefined
      : 'asc'

    if (!order) {
      props.onChangeSorting?.(null)
    }

    if (column.sorterKey && order) {
      props.onChangeSorting?.({
        key: column.sorterKey,
        order
      })
    }
  }

  const parsedColumns: Column<TData>[] = props.columns.map(column => {
    let parsedColumn = column

    parsedColumn = {
      ...parsedColumn,
      title: parsedColumn.title ?? parsedColumn.label
    }
    if (column.sorterKey)
      parsedColumn = {
        ...parsedColumn,
        title: (
          <Sorter sorterKey={column.sorterKey} sorting={props.sorting || null}>
            {parsedColumn.title}
          </Sorter>
        ),
        onHeaderCell: () => ({
          onClick: () => changeSorting(column),
          role: 'button',
          ['aria-sort']:
            props.sorting?.key === column.key
              ? props.sorting?.order === 'desc'
                ? 'descending'
                : 'ascending'
              : 'none'
        })
      }
    if (column.isHighlighted)
      parsedColumn = {
        ...parsedColumn,
        className: clsx(parsedColumn.className, styles.highlightedCell)
      }

    if (!column.filled)
      parsedColumn = {
        ...parsedColumn,
        className: clsx(parsedColumn.className, styles.withPadding)
      }
    if (column.cellDataTestId)
      parsedColumn = {
        ...parsedColumn,
        onCell: () =>
          // data test id is not defined in html attributes
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          ({
            ['data-test-id']: column.cellDataTestId
          }) as HTMLAttributes<HTMLTableCellElement>
      }

    return parsedColumn
  })

  const actionColumn: Column<TData> = {
    title: '',
    key: 'actions',
    className: 'actionCell',
    render: (_, record) => {
      const actions =
        typeof props.actions === 'function'
          ? props.actions?.(record)
          : props.actions || []

      return actions.length ? (
        <ActionCell actions={actions} record={record} />
      ) : undefined
    }
  }

  const isSelectable = props.rowSelection

  const isFirstColumnFixed = props.columns[0]?.fixed

  const selectAllValue =
    isSelectable?.selectedItems.length === isSelectable?.count &&
    !!isSelectable?.selectedItems.length

  const checkboxColumn: Column<TData> = {
    title: (
      <div className={styles.checkboxColumn}>
        <Checkbox
          id="select-all"
          disabled={isSelectable?.disabled}
          variant={isSelectable?.checkboxVariant || 'dark'}
          label={
            typeof isSelectable?.labelHeader === 'function'
              ? isSelectable.labelHeader(selectAllValue)
              : isSelectable?.labelHeader
          }
          hideLabel
          checked={isSelectable?.allCheckboxValue}
          className={styles.checkbox}
          value={selectAllValue}
          onChange={value => {
            isSelectable?.onSelectAll(value)
          }}
        />
      </div>
    ),
    key: 'id',
    className: styles.actionCell,
    width: TABLE_CELL_WIDTH.ACTIONS,
    render: row => (
      <div
        className={styles.checkboxColumn}
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
      >
        <Checkbox
          id={row[rowKey]}
          hideLabel
          label={isSelectable?.labelItem({
            ...row,
            selected: isSelectable?.selectedItems.includes(row[rowKey])
          })}
          disabled={isSelectable?.disabled}
          value={isSelectable?.selectedItems.includes(row[rowKey])}
          className={styles.checkbox}
          onChange={value => {
            isSelectable?.onSelect(value, row[rowKey])
          }}
        />
      </div>
    )
  }

  const columns = [
    isSelectable ? checkboxColumn : [],
    parsedColumns,
    typeof props.actions === 'function' ||
    (props.actions && props.actions.length)
      ? actionColumn
      : []
  ].flat()

  const currentOrder = props.sorting?.order

  const handleOnPageSize = (value?: number) => {
    if (!value) return

    props.onChangePageSize?.(value)
    props.onChangePage?.(1)
  }

  const handleOnTab = (event: React.KeyboardEvent<HTMLTableSectionElement>) => {
    if (event.key === 'Tab') {
      // Scrolls table body while scrolling the header.
      // It fixes the issue of columns misaligning with headers when navigating through the header.
      body?.scroll({
        left: header?.scrollLeft
      })
    }
  }
  const components = {
    header: {
      wrapper: (headerProps: HTMLAttributes<HTMLTableSectionElement>) => (
        <thead
          {...headerProps}
          onKeyUp={handleOnTab}
          className={clsx(headerProps.className, styles.tableHead)}
        />
      )
    },
    body: {
      cell: (cellProps: HTMLAttributes<HTMLTableCellElement>) => (
        <td
          {...cellProps}
          className={clsx(cellProps.className, styles.tableCell)}
        />
      )
    },
    table: ({ children, ...tableProps }: HTMLAttributes<HTMLTableElement>) => (
      <table {...tableProps}>
        <VisuallyHidden asChild>
          <caption>{props.caption}</caption>
        </VisuallyHidden>
        {children}
      </table>
    ),
    ...props.components
  }

  const rowKey = props.rowKey || 'id'

  const getExpandedRowId = (id: unknown) => `${id}-expanded-row`

  return (
    <div className={styles.tableContainer}>
      {showTopBar ? (
        <TopBar top={props.topBarTop}>
          {props.leftTopBar === undefined ? (
            <TableTopBarCount
              count={props.count || 0}
              totalCount={props.totalCount}
              selectedItemsLength={
                props.rowSelection?.selectedItems.length || 0
              }
            />
          ) : (
            props.leftTopBar
          )}

          {props.rightTopBar}
        </TopBar>
      ) : null}

      <div
        className={clsx(
          styles.table,
          props.withBorders && styles.tableWithBorders,
          isFirstColumnFixed && styles.withFixedColumn,
          !props.noRowHoverEffect && styles.withHoverableRow
        )}
        ref={refTable}
        style={
          {
            '--first-column-width': isFirstColumnFixed
              ? `${props.columns[0].width}px`
              : '0px',
            '--header-position':
              props.headerTop !== undefined
                ? `${props.headerTop}px`
                : `${headerTop}px`
          } as React.CSSProperties
        }
      >
        <RcTable
          id={props.id}
          columns={columns}
          data={props.data}
          className={props.tableClassName}
          showHeader={props.showHeader}
          rowClassName={row =>
            clsx(
              isSelectable?.selectedItems.includes(row[rowKey] as string) &&
                styles.selectedRow,
              props.rowClassName
            )
          }
          expandable={
            props.expandedRowRender
              ? {
                  expandIcon: expandProps => (
                    <div className={styles.actionColumnWrapper}>
                      <ButtonIcon
                        size="medium"
                        dataTestId="expand-row"
                        onClick={event =>
                          expandProps.onExpand(expandProps.record, event)
                        }
                        className={styles.iconWrapper}
                        variant="tertiary"
                        ariaControls={getExpandedRowId(expandProps.record.id)}
                        ariaExpanded={expandProps.expanded}
                        ariaLabel={t(
                          expandProps.expanded
                            ? 'button.collapse-row'
                            : 'button.expand-row'
                        )}
                      >
                        {expandProps.expanded ? (
                          <ChevronSmallUp className={styles.icon} />
                        ) : (
                          <ChevronSmallDown className={styles.icon} />
                        )}
                      </ButtonIcon>
                    </div>
                  ),
                  columnTitle: '',
                  onExpandedRowsChange: keys => setExpandedRowKeys([...keys]),
                  expandedRowKeys,
                  columnWidth: TABLE_CELL_WIDTH.ACTIONS,
                  expandedRowRender: expandedRowProps => (
                    <div
                      className={styles.expandedRow}
                      id={getExpandedRowId(expandedRowProps.id)}
                    >
                      {props.expandedRowRender?.(expandedRowProps)}
                    </div>
                  )
                }
              : undefined
          }
          onRow={(data: TData) => ({
            onClick: () => props.onRowClick?.(data),
            onKeyDown: e => {
              if (e.key === 'Enter' || e.key === ' ') props.onRowClick?.(data)
            }
          })}
          components={components}
          rowKey={rowKey}
          scroll={{
            x: props.tableWidth || '100%',
            y: props.tableHeight || '100%'
          }}
          emptyText={
            props.isLoading ? (
              <div className={styles.loader}>
                <Spinner />
              </div>
            ) : (
              'No data'
            )
          }
        />

        {props.pagination && props.count ? (
          <div className={styles.pagination}>
            <div>
              <Pagination
                id={props.id}
                page={props.pagination.page}
                count={props.count}
                pageSize={props.pagination.pageSize}
                onPageChange={props.onChangePage}
              />
            </div>
            <div className={styles.selectItems}>
              <Label
                id={'select-for-items-' + props.id}
                label={t('label.items-per-screen')}
                className={styles.selectItemsLabel}
              />
              <Select
                className={styles.selectItemsSelect}
                options={selectOptions}
                hideSearch
                id={'select-for-items-' + props.id}
                value={props.pagination.pageSize}
                onChange={handleOnPageSize}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

type TopBarProps = {
  children: React.ReactNode
  top?: number
}

const TopBar = (props: TopBarProps) => {
  const refTopBar = useRef<HTMLDivElement>(null)

  const topBarTop = useStickyElement({
    elementRef: refTopBar,
    priority: PRIORITY.tableTopBar
  })

  return (
    <>
      <Divider variant="light" />
      <div
        ref={refTopBar}
        className={styles.topBar}
        style={{
          top: props.top ?? topBarTop
        }}
      >
        {props.children}
      </div>
    </>
  )
}

type ActionCellProps<TData> = {
  actions: TableAction<TData>[]
  record: TData
}

const ActionCell = <TData,>(props: ActionCellProps<TData>) => {
  const { t } = useTranslation('common')
  return (
    <Dropdown
      sideOffset={-10}
      trigger={
        <ButtonIcon
          variant="tertiary"
          className={styles.iconWrapper}
          dataTestId="actions-button"
          size="small"
          ariaLabel={t('button.actions-menu')}
        >
          <MoreOutlined className={styles.icon} />
        </ButtonIcon>
      }
    >
      {props.actions
        ?.filter(action =>
          typeof action?.isHidden === 'function'
            ? !action.isHidden(props.record)
            : !action?.isHidden
        )
        ?.map((action, index) => (
          <DropdownMenuItem
            className={clsx(
              styles.action,
              action.variantAction === 'danger' && styles.actionDanger
            )}
            onClick={() => action.onClick(props.record)}
            key={index}
          >
            {action.icon ? (
              <span className={styles.actionIcon}>{action.icon}</span>
            ) : null}
            <span>{action.text}</span>
          </DropdownMenuItem>
        ))}
    </Dropdown>
  )
}

type SorterProps = {
  sorterKey: React.Key
  sorting: Sorting | null
  children: React.ReactNode
}

const Sorter = (props: SorterProps) => {
  const shouldHide = (order: Order) =>
    props.sorting !== null &&
    props.sorting.key === props.sorterKey &&
    props.sorting.order !== order
  return (
    <div className={styles.headerSorted}>
      {props.children}
      <div className={styles.sorter}>
        <TriangleUp
          className={clsx(
            styles.sorterIcon,
            shouldHide('asc') && styles.sorterIconHidden
          )}
        />
        <TriangleDown
          className={clsx(
            styles.sorterIcon,
            shouldHide('desc') && styles.sorterIconHidden
          )}
        />
      </div>
    </div>
  )
}

export default Table
